import { femaleQuestionMedium, maleQuestionMedium, bulbMedium } from '@/assets/icons';
import { WITHOUT_DATA, WITHOUT_DATA_SOURCE } from './quotes';

const INDICATORS = `
<p>
  La gestión del rendimiento a través de la mejora continua debe estar centrada en el cliente. En cuanto a la creación de valor de la empresa, la necesidad del cliente debe ser la prioridad absoluta.
  El cliente puede ser la entidad a la que se vende un producto, como resultado de una transacción, pero también puede tratarse de un cliente interno, es decir, otro equipo o una entidad perteneciente a la misma empresa.<br>
  La gestión del rendimiento se basa en indicadores fuertes, que permiten al equipo controlar sus rendimientos y adoptar las acciones necesarias en caso de que exista una diferencia respecto al objetivo.
</p>
<h2 class="basic-sub-title">
  ¿Qué es un indicador?
</h2>
<div class="ia-sensei-quote">
  <span class="quote">"${WITHOUT_DATA}"</span>
  <br>
  <span class="author">${WITHOUT_DATA_SOURCE}</span>
</div>
<p>
  Hoy en día se toman demasiadas decisiones basándose en opiniones. Una buena gestión del rendimiento permite acabar con las suposiciones al sustituirlas por datos tangibles.
  Así, la medición permite recabar el dato y ser objetivos respecto a un proceso.
</p>
<ul>
  La medición puede ser:
  <li>en tiempo real: a través de un sistema SPC (control estadístico de procedimientos) por ejemplo, que recoja el dato en tiempo real,</li>
  <li>periódico: cada hora, se cuenta el número de piezas realizadas,</li>
  <li>por muestreo o sondeo: para cuantificar la satisfacción del cliente, por ejemplo.</li>
</ul>
<p>
  Sea cual sea el tipo de medición o su periodicidad, se va a correlacionar con un indicador preciso (o KPI, siglas de Key Performance Indicator).
  Un buen indicador es aquel relacionado de una u otra forma con la satisfacción del cliente.<br>
  Podemos utilizar dos tipos de indicadores: Los indicadores de procesos y los indicadores de resultados.
  Deben decirnos si el resultado es conforme y si se ha seguido el proceso correctamente.<br>
  Aunque generalmente en las empresas se utilizan indicadores de resultados, con frecuencia carecen de indicadores de procesos, aunque muchas veces se produzcan problemas de calidad o de incumplimiento de plazo precisamente por no haber seguido un proceso.
</p>
<div class="ia-sensei-example">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${bulbMedium}"></path></svg>
  <div class="example-title">Por ejemplo:</div>
  Vamos a seguir una receta de cocina para hacer un pastel. Por supuesto, la receta nos indicará los indicadores de procesos correctos que podremos seguir, mientras que el resultado de la preparación del pastel es el pastel en sí.
  Como indicadores de procesos, podremos pues elegir la temperatura del horno, la duración del horneado, el orden de los ingredientes... y los indicadores de resultados podrán ser
  el tamaño y la consistencia del pastel, preguntar a los invitados qué les ha parecido, etc.
</div>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">Ya hemos terminado con los indicadores. ¿Nos ponemos manos a la obra?</span>
</div>
<p>
  Un momentito.<br>
  Un cosa es saber qué tipo de datos se recopilan. Ahora bien, debemos ser capaces de
  tomar decisiones y de actuar con rapidez cuando algo no va bien. El simple hecho de mirar
  los datos no nos dirá si hay un problema, salvo si somos expertos en análisis de datos.<br>
  Es ahí cuando entra en juego el objetivo.
</p>
<h2 class="basic-sub-title">
  Objetivo SMART
</h2>
<p>
  Resulta fundamental definir correctamente un objetivo cuando hablamos de indicadores,
  pues suele hacérseles un seguimiento diario.<br>
  <strong>SMART</strong> son siglas en inglés utilizadas para saber cómo marcar un objetivo y cómo comunicar al respecto.<br>
</p>
<ul>
  <li>
    <strong>S</strong> de específico (Specific): El objetivo debe ser concreto, estar cuantificado. Ser vagos no nos permite disponer
    de una visión sobre los resultados. Por ejemplo: "Reducir el número de defectos" no es un objeto específico.<br>
    "Limitar el número de errores a 12 al día": Este objetivo permite a todos saber si ha sido un buen día
    o si, al contrario, hay aspectos que pueden mejorar.
  </li>
  <div class="ia-sensei-example">
    Tomemos la temperatura del horno durante el horneado.
  </div>
  <li>
    <strong>M</strong> de medible (Mesurable): Redundando en esta necesidad de precisión, todos deben ser capaces de medir
    la diferencia del indicador de forma precisa.
  </li>
  <div class="ia-sensei-example">
    Un termómetro nos servirá.
  </div>
  <li>
    <strong>A</strong> de alcanzable (Atteignable): El objetivo debe resultar alcanzable o factible. A la hora de definir un objetivo,
    deberemos dejar de lado la ambición, pues un objetivo ambicioso está reñido con la gestión
    diaria del rendimiento. De hecho, el equipo correría el riesgo de encontrarse con valores demasiado alejados de los objetivos.
  </li>
  <div class="ia-sensei-example">
    Podríamos decir que... ¿es tan fácil como hacer un pastel?
  </div>
  <li>
    <strong>R</strong> de realista (Realistic): El objetivo debe estar en consonancia con los recursos disponibles, es decir,
    ser realista.
  </li>
  <div class="ia-sensei-example">
    Comprobemos si hemos regulado bien el horno... ¡Todo correcto!
  </div>
  <li>
    <strong>T</strong> de tiempo (Time): El objetivo debe alcanzarse en un momento preciso. Volviendo
    al ejemplo "Reducir el número de errores", es imposible saber cuándo se ha alcanzado el objetivo.
    En la gestión diaria del rendimiento, se establece un valor que alcanzar (o NO alcanzar)
    en un día determinado.
  </li>
  <div class="ia-sensei-example">
    Duración indicada en la receta: 20 minutos.
  </div>
</ul>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">Entonces, si se supera el objetivo, ¿debo notificar un problema?<span>
</div>
<p>
  Exacto. Superar un objetivo puede significar 2 cosas: O bien que el objetivo está mal definido,
  lo que puede darse al establecer nuevos indicadores, o bien que ha surgido un problema.<br>
  Notifique que existe un problema para investigar y dar con la causa raíz mediante las herramientas que ofrece Shizen.
</p>
`;

const INDICATORS_TITLE = 'Indicadores y objetivos';

export default {
  INDICATORS,
  INDICATORS_TITLE,
};
